import TextInput from "@builder/components/general/TextInput";
import "./style.scss";
import { useEffect, useMemo, useState } from "react";
import OliverText from "@builder/components/general/OliverText";
import {
    actionsCategoriesForUI,
    FunctionType,
    getFunctionCategory,
    getIconByType,
    getSubtitleByType,
    getTitleByType
} from "@builder/utils/actionBuilderHelpers";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { ActionEditorItem, setCurrent, updateStepperActions } from "@builder/store/slices/actionBuilderSlice";
import { useTranslation } from "react-i18next";
import { useGetExtensionsQuery } from "@builder/store/slices/extensionsApiSlice/extensionsApiSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetCompanyExtensionsQuery } from "@builder/store/slices/extensionsApiSlice/companyExtensionApiSlice";
import { isExtensionInstalled } from "@builder/store/slices/extensionsApiSlice/extension.util";

const Function = () => {
    const selectedStep = useAppSelector(state => state.actionBuilder.current.id);
    const [searchValue, setSearchValue] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("All");
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    // const company = useAppSelector(state => state.storeData.data?.company);
    const companyId = useAppSelector(state => state.builder.build.companyId);

    const { data: extensions } = useGetExtensionsQuery(companyId ? companyId : skipToken);
    const { data: companyExtensions } = useGetCompanyExtensionsQuery(companyId ? companyId : skipToken);

    useEffect(() => {
        setSelectedCategory("All");
    }, [selectedStep]);

    const itemsByCategory = useMemo(() => {
        return actionsCategoriesForUI.reduce<Record<string, FunctionType[]>>((acc, item) => {
            if (item.extensionGuid) {
                if (!isExtensionInstalled(extensions || [], companyExtensions || [], item.extensionGuid)) {
                    return acc;
                }
            }

            if (!acc[item.group]) {
                acc[item.group] = [];
            }
            acc[item.group].push(item);
            return acc;
        }, {});
    }, [extensions, companyExtensions]);

    const filteredItemsByCategory = Object.keys(itemsByCategory).reduce<Record<string, FunctionType[]>>((acc, category) => {
        acc[category] = itemsByCategory[category].filter(item => getTitleByType(item.type).toLowerCase().includes(searchValue.toLowerCase()));
        return acc;
    }, {});

    const categories = ["All", ...Object.keys(itemsByCategory)];
    const categoriesToDisplay = selectedCategory === "All" ? Object.keys(filteredItemsByCategory) : [selectedCategory];

    const handleEdit = (item: FunctionType) => {
        const updatedItem = {
            type: item.type,
            title: item.group === "thirdParty" ? getTitleByType(item.type, true) : `Action: ${getTitleByType(item.type, true)}`,
            subtitle: null
        };
        dispatch(updateStepperActions({ id: selectedStep as string, item: updatedItem as ActionEditorItem }));
        dispatch(setCurrent({ tab: item.type, id: selectedStep }));
    };

    return (
        <div className="function-wrapper">
            <TextInput
                value={searchValue}
                placeholder="Search for actions"
                callback={setSearchValue}
                icon={{ src: "/search-icon.svg" }}
                className="actions-tab-input"
            />
            <div className="category-tabs">
                {categories.map(categoryName => (
                    <div
                        key={categoryName}
                        className={`category-tab ${selectedCategory === categoryName ? "active" : ""}`}
                        onClick={() => setSelectedCategory(categoryName)}
                    >
                        <OliverText text={categoryName} translatePrefix="popup.actionBuilder." size="xs" weight="light" color="base-blue" />
                    </div>
                ))}
            </div>
            {categoriesToDisplay.map((category, i) => {
                if (filteredItemsByCategory[category] && filteredItemsByCategory[category].length > 0) {
                    return (
                        <div className="function-category-wrapper" key={`category-${i}`}>
                            <OliverText
                                text={t("popup.actionBuilder." + getFunctionCategory(category))}
                                size="xs"
                                color="base-grey"
                                className="cat-name-divider"
                            />
                            <div className="function-category-item-wrapper">
                                {filteredItemsByCategory[category].map(item => (
                                    <div
                                        className={`function-item-wrapper ${item.disabled ? "disabled" : ""}`}
                                        key={`function-${item.id}`}
                                        onClick={() => !item.disabled && handleEdit(item)}
                                    >
                                        <img src={getIconByType(item.type)} />
                                        <div>
                                            <OliverText text={getTitleByType(item.type)} size="s" weight="medium-heavy" />
                                            <OliverText
                                                text={getSubtitleByType(item.type)}
                                                size="xs"
                                                color="base-grey"
                                                translatePrefix="popup.actionBuilder."
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }
                return null;
            })}
        </div>
    );
};

export default Function;
